/**
 * SickLeaveCalendar.js
 *
 * Single-month expanded calendar displaying sick leave days with red backgrounds,
 * staff avatar badges with initials, and a person count label.
 * Navigation (month + year) is controlled externally by SickLeavePage.
 * Clicking a sick-leave day calls onDayClick(dateString) where
 * dateString is in YYYY-MM-DD format.
 *
 * Mirrors: TeamVacationCalendar.js
 * Differences:
 * - Red color scheme instead of yellow/orange
 * - Single-month view driven by year + month props — no internal navigation
 * - No holiday integration
 * - No internal day panel — onDayClick delegates to SickLeavePage
 * - onDayClick receives dateString, not a Date object
 *
 * RESPONSIVE: Avatar count is capped at 2 on screens narrower than 480px,
 * 4 otherwise. The grid sits inside a scroll wrapper so very narrow viewports
 * can scroll horizontally rather than collapsing cells below a usable size.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import '../../styles/sick-leave.css';

/**
 * Returns true while the given CSS media query matches the current viewport.
 * Defaults to false during SSR when window is unavailable.
 */
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia(query);
    const listener = (e) => setMatches(e.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [query]);

  return matches;
};

const SickLeaveCalendar = ({
  year,
  month,
  sickLeave = [],
  staff = [],       // accepted for API consistency with parent; used by SickLeaveDayPanel
  onDayClick
}) => {
  const { t, i18n } = useTranslation('sickLeave');

  /**
   * Cap displayed avatars at 2 on screens narrower than 480px, 4 otherwise.
   * Prevents avatar badges from wrapping awkwardly inside compact cells.
   */
  const isSmallScreen = useMediaQuery('(max-width: 480px)');
  const maxAvatars = isSmallScreen ? 2 : 4;

  /**
   * Convert Date object to YYYY-MM-DD string using LOCAL time.
   * Avoids timezone conversion issues.
   */
  const formatDateToString = useCallback((date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, []);

  /**
   * Group sick leave entries by date string for O(1) day lookups.
   */
  const sickLeaveByDate = useMemo(() => {
    const grouped = new Map();
    sickLeave.forEach(entry => {
      const dateStr = entry.getDateString ? entry.getDateString() : entry.date;
      if (!dateStr) return;
      if (!grouped.has(dateStr)) grouped.set(dateStr, []);
      grouped.get(dateStr).push(entry);
    });
    return grouped;
  }, [sickLeave]);

  /**
   * Get days in month.
   * Returns array of Date objects for each day in the given month.
   */
  const getDaysInMonth = useCallback((date) => {
    const y = date.getFullYear();
    const m = date.getMonth();
    const lastDay = new Date(y, m + 1, 0);
    const days = [];
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(y, m, i));
    }
    return days;
  }, []);

  /**
   * Get the starting day of week for the month.
   * Adjusted for Monday-first calendar (Monday = 0, Sunday = 6).
   */
  const getFirstDayOfMonth = useCallback((date) => {
    const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
    const dayOfWeek = firstDay.getDay();
    return dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  }, []);

  /**
   * Derive a stable Date object for the displayed month from year + month props.
   * Avoids repeated construction in downstream memos.
   */
  const currentMonthDate = useMemo(
    () => new Date(year, month, 1),
    [year, month]
  );

  /**
   * Generate calendar grid with null padding cells for Monday-first alignment.
   * Mirrors the pattern in TeamVacationCalendar.
   */
  const calendarDays = useMemo(() => {
    const days = getDaysInMonth(currentMonthDate);
    const firstDayOfWeek = getFirstDayOfMonth(currentMonthDate);
    const emptyDays = Array(firstDayOfWeek).fill(null);
    return [...emptyDays, ...days];
  }, [currentMonthDate, getDaysInMonth, getFirstDayOfMonth]);

  /**
   * Check if a Date object represents today.
   */
  const isToday = useCallback((date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }, []);

  /**
   * Get two-letter initials from a display name.
   * Returns '??' for missing, loading, or deleted staff names.
   */
  const getInitials = useCallback((name) => {
    if (
      !name ||
      name === 'Lädt...' ||
      name === 'Unbekannter Mitarbeiter' ||
      name === 'Gelöschter Mitarbeiter'
    ) {
      return '??';
    }
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }, []);

  /**
   * Weekday headers — generated dynamically from the active locale.
   * Uses Jan 5, 2015 (a Monday) as a stable reference week start.
   */
  const weekDays = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' });
    return Array.from({ length: 7 }, (_, i) =>
      formatter.format(new Date(2015, 0, 5 + i))
    );
  }, [i18n.language]);

  return (
    <div className="sl-calendar-wrapper">

      {/* Weekday Header Row */}
      <div className="sl-calendar-weekday-header">
        {weekDays.map((day, index) => (
          <div key={`wh-${index}`} className="sl-calendar-weekday-label">
            {day}
          </div>
        ))}
      </div>

      {/* Day Grid */}
      <div className="sl-calendar-grid">
        {calendarDays.map((date, index) => {

          /* Padding cell — renders before the first day of the month */
          if (!date) {
            return (
              <div key={`empty-${index}`} className="sl-calendar-day empty" />
            );
          }

          const dateStr = formatDateToString(date);
          const entries = sickLeaveByDate.get(dateStr) || [];
          const hasSickLeave = entries.length > 0;
          const isTodayDate = isToday(date);

          /*
           * Unique staff names drive both the avatar display and overflow count.
           * Deduplication prevents showing the same initials twice if a staff
           * member has multiple entries on the same day (edge case).
           */
          const uniqueNames = [...new Set(entries.map(e => e.staffName || ''))];
          const displayed = uniqueNames.slice(0, maxAvatars);
          const overflow = uniqueNames.length - maxAvatars;

          const cssClasses = [
            'sl-calendar-day',
            hasSickLeave && 'has-sick-leave',
            isTodayDate && 'today'
          ].filter(Boolean).join(' ');

          return (
            <div
              key={dateStr}
              className={cssClasses}
              onClick={() => hasSickLeave && onDayClick && onDayClick(dateStr)}
              role={hasSickLeave ? 'button' : undefined}
              tabIndex={hasSickLeave ? 0 : -1}
              aria-label={
                date.toLocaleDateString(i18n.language, {
                  day: 'numeric',
                  month: 'long'
                }) +
                (hasSickLeave
                  ? `, ${entries.length} ${t('sickDays')}`
                  : '')
              }
              onKeyDown={(e) => {
                if (hasSickLeave && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onDayClick && onDayClick(dateStr);
                }
              }}
            >
              {/* Day Number — blue fill applied via CSS when today */}
              <div className="sl-day-number">
                {date.getDate()}
              </div>

              {/* Avatar badges and count — only rendered on sick leave days */}
              {hasSickLeave && (
                <>
                  <div className="sl-avatar-row">
                    {displayed.map((name, idx) => (
                      <span key={idx} className="sl-avatar">
                        {getInitials(name)}
                      </span>
                    ))}
                    {overflow > 0 && (
                      <span className="sl-avatar overflow">
                        +{overflow}
                      </span>
                    )}
                  </div>

                  <div className="sl-day-count">
                    {t('personCount', { count: entries.length })}
                  </div>
                </>
              )}

            </div>
          );
        })}
      </div>

    </div>
  );
};

export default SickLeaveCalendar;